/**
 * Detalhe da medição, composto no front.
 *
 * O faturamento-api só devolve o que é dele (ciclo, transações, Omie). O que a tela mostra por
 * coleta vem de três lugares: situação, peso, embalagem e unidade da coleta do musa-agreements
 * (por `collectId[]`, ou por período + unidades na fatura de valor fixo), unidade de medida do
 * material no catálogo do agreements, e MTR do mtr-manager. Falha numa fonte secundária degrada
 * o campo (nome = id, MTR = desconhecido), nunca derruba a tela.
 */

import {
  fetchCollects,
  fetchRegistryMaterials,
  fetchRegistryPackagings,
  type CollectsItem,
  type RegistryMaterial,
  type RegistryPackaging,
} from '@/integrations/agreements/client';
import {
  fetchInvoiceCollectRows,
  fetchInvoiceDetailHeader,
  type InvoiceCollect,
  type InvoiceCollectRow,
  type InvoiceCollectsPage,
  type InvoiceDetail,
  type InvoiceDetailHeader,
  type InvoiceDetailsFilterInput,
  type InvoiceDetailsResponse,
  type Pagination,
} from '@/integrations/faturamento/client';
import { fetchManifestsByCollectIds } from '@/integrations/mtrManager/client';

/** Mesmo fator do gateway para a fatura variável: a linha do faturamento só tem o peso. */
const VOLUME_MULTIPLIER = 1.111;

/** Rótulos que a tela já tem no store: unidades do `/generators/me`. */
export interface InvoiceLookups {
  establishments?: Array<{ id: string; name: string }>;
}

interface Registry {
  materials: Map<number, RegistryMaterial>;
  packagings: Map<number, RegistryPackaging>;
}

let registryCache: Promise<Registry> | null = null;

/** Catálogo do agreements, uma vez por sessão; falha não fica em cache. */
export function loadRegistry(): Promise<Registry> {
  if (!registryCache) {
    registryCache = Promise.all([fetchRegistryMaterials(), fetchRegistryPackagings()])
      .then(([materials, packagings]) => ({
        materials: new Map(materials.map((m) => [m.id, m])),
        packagings: new Map(packagings.map((p) => [p.id, p])),
      }))
      .catch((error) => {
        registryCache = null;
        throw error;
      });
  }
  return registryCache;
}

/** Só para testes. */
export function resetRegistryCache(): void {
  registryCache = null;
}

async function secondary<T>(label: string, promise: Promise<T>, fallback: T): Promise<T> {
  try {
    return await promise;
  } catch (error) {
    console.warn(`[invoiceDetails] ${label} indisponível; campo degradado`, error);
    return fallback;
  }
}

type MtrByCollect = Map<string, string | null>;

/** Número do MTR por coleta; `null` quando o mtr-manager não respondeu. */
async function loadMtrNumbers(collectIds: string[]): Promise<MtrByCollect | null> {
  if (!collectIds.length) return new Map();
  return secondary(
    'mtr-manager',
    fetchManifestsByCollectIds(collectIds).then(
      (rows) => new Map(rows.map((row) => [row.collect_id, row.has_mtr ? row.mtr_number : null])),
    ),
    null,
  );
}

function mtrNumbersFor(collectId: string, mtr: MtrByCollect | null): string[] | null {
  if (!mtr) return null;
  const number = mtr.get(collectId);
  return number ? [number] : [];
}

function materialOf(
  item: CollectsItem | undefined,
  materialId: number | null | undefined,
  registry: Registry | null,
): InvoiceCollect['material'] {
  const id = item?.material?.id ?? materialId ?? null;
  if (id == null) return null;
  const fromRegistry = registry?.materials.get(id);
  return {
    id,
    name: item?.material?.name ?? fromRegistry?.name ?? String(id),
    unity: fromRegistry?.unity ?? '',
  };
}

function packagingOf(
  item: CollectsItem | undefined,
  packagingId: number | null | undefined,
  registry: Registry | null,
): InvoiceCollect['packaging'] {
  const id = item?.packaging?.id ?? packagingId ?? null;
  if (id == null) return null;
  return { id, name: item?.packaging?.name ?? registry?.packagings.get(id)?.name ?? String(id) };
}

function establishmentOf(
  item: CollectsItem | undefined,
  generatorId: number | null | undefined,
  lookups: InvoiceLookups,
): InvoiceCollect['establishment'] {
  if (item?.establishment) return { id: item.establishment.id, name: item.establishment.name };
  if (generatorId == null) return null;
  const known = lookups.establishments?.find((e) => e.id === String(generatorId));
  return { id: generatorId, name: known?.name ?? String(generatorId) };
}

const round3 = (value: number) => Math.round(value * 1000) / 1000;

/** Linha da fatura variável: valor e transação do faturamento, o resto do agreements (quando ele conhece a coleta). */
function fromFaturamentoRow(
  row: InvoiceCollectRow,
  item: CollectsItem | undefined,
  mtr: MtrByCollect | null,
  registry: Registry | null,
  lookups: InvoiceLookups,
): InvoiceCollect {
  const weight = item?.weight ?? row.units_collected ?? null;
  return {
    id: row.collect_id,
    transaction_id: row.transaction_id ?? null,
    planned_date: item?.planned_date ?? row.event_date.slice(0, 10),
    status: item?.status ?? null,
    value: row.value,
    units: row.units ?? item?.units ?? null,
    units_collected: row.units_collected ?? item?.weight ?? null,
    weight,
    volume: item?.volume ?? (weight != null ? round3(weight * VOLUME_MULTIPLIER) : null),
    material: materialOf(item, row.material_id, registry),
    packaging: packagingOf(item, row.packaging_id, registry),
    establishment: establishmentOf(item, row.generator_id, lookups),
    mtr_numbers: mtrNumbersFor(row.collect_id, mtr),
  };
}

/** Linha da fatura fixa: tudo do agreements; não há transação nem valor por coleta. */
function fromAgreementsItem(item: CollectsItem, mtr: MtrByCollect | null, registry: Registry | null): InvoiceCollect {
  return {
    id: item.id,
    transaction_id: null,
    planned_date: item.planned_date,
    status: item.status ?? null,
    value: null,
    units: item.units ?? null,
    units_collected: item.weight ?? null,
    weight: item.weight ?? null,
    volume: item.volume ?? null,
    material: materialOf(item, null, registry),
    packaging: packagingOf(item, null, registry),
    establishment: establishmentOf(item, null, {}),
    mtr_numbers: mtrNumbersFor(item.id, mtr),
  };
}

const emptyPage = (page: number, pageSize: number): InvoiceCollectsPage => ({
  data: [],
  pagination: { page, last_page: 0, page_size: pageSize, count: 0 },
});

export interface ComposeInvoiceCollectsParams {
  id: string;
  header: InvoiceDetailHeader;
  page: number;
  pageSize: number;
  lookups?: InvoiceLookups;
}

/**
 * Uma página de coletas da fatura, no shape que a tela e o CSV leem.
 *
 * - Valor fixo: o faturamento não tem transação por coleta. As coletas são as realizadas das
 *   unidades da config no período, listadas no agreements (`establishment_id` exato, como o
 *   gateway fazia com `generator_id IN (...)`).
 * - Valor variável: linhas do faturamento (valor, transação) enriquecidas pelo agreements por
 *   `collectId[]`; coleta que o agreements não devolve (fora do escopo, apagada) fica com os
 *   rótulos do catálogo e do store.
 */
export async function composeInvoiceCollectsPage({
  id,
  header,
  page,
  pageSize,
  lookups = {},
}: ComposeInvoiceCollectsParams): Promise<InvoiceCollectsPage> {
  if (header.is_fixed_value) {
    if (!header.period || !header.generator_ids.length) return emptyPage(page, pageSize);
    const result = await fetchCollects({
      page,
      page_size: pageSize,
      date_from: header.period.start,
      date_to: header.period.end,
      establishment_id: header.generator_ids,
      status: ['collected'],
    });
    const items = result.data ?? [];
    const [mtr, registry] = await Promise.all([
      loadMtrNumbers(items.map((item) => item.id)),
      secondary('catálogo', loadRegistry(), null),
    ]);
    const pagination: Pagination = result.pagination ?? { page, last_page: items.length ? page : 0, page_size: pageSize, count: items.length };
    return { data: items.map((item) => fromAgreementsItem(item, mtr, registry)), pagination };
  }

  const rows = await fetchInvoiceCollectRows(id, page, pageSize);
  if (rows.source === 'agreements') return emptyPage(page, pageSize);

  const collectIds = rows.data.map((row) => row.collect_id);
  const [itemsById, mtr, registry] = await Promise.all([
    collectIds.length
      ? secondary(
          'musa-agreements',
          fetchCollects({ page: 1, page_size: 500, collect_id: collectIds }).then(
            (result) => new Map((result.data ?? []).map((item) => [item.id, item])),
          ),
          new Map<string, CollectsItem>(),
        )
      : Promise.resolve(new Map<string, CollectsItem>()),
    loadMtrNumbers(collectIds),
    secondary('catálogo', loadRegistry(), null),
  ]);

  return {
    data: rows.data.map((row) => fromFaturamentoRow(row, itemsById.get(row.collect_id), mtr, registry, lookups)),
    pagination: rows.pagination,
  };
}

export function toInvoiceDetail(header: InvoiceDetailHeader): InvoiceDetail {
  const invoice = header.invoice;
  return {
    sfm_id: invoice.sfm_id,
    os_code: invoice.os_code ?? null,
    numero_pedido: invoice.numero_pedido ?? null,
    billing_period: invoice.billing_period ?? '',
    value: invoice.value,
    due_date: invoice.due_date ?? null,
    status: invoice.status ?? null,
    release_status: invoice.release_status ?? null,
    release_at: invoice.release_at ?? null,
    published_at: invoice.published_at ?? null,
    po_number: invoice.po_number ?? null,
    po_informed_by_name: invoice.po_informed_by_name ?? null,
    po_informed_at: invoice.po_informed_at ?? null,
    requires_po: invoice.requires_po ?? false,
    awaiting_po: invoice.awaiting_po ?? false,
    is_fixed_value: header.is_fixed_value,
    paying_generator: invoice.paying_generator ?? null,
    nf_data: invoice.nf_data ?? [],
    cr_data: invoice.cr_data ?? [],
  };
}

/** Detalhe completo no shape que a tela sempre leu: cabeçalho do faturamento + página de coletas composta. */
export async function fetchInvoiceDetails(
  filter: InvoiceDetailsFilterInput,
  lookups: InvoiceLookups = {},
): Promise<InvoiceDetailsResponse> {
  const header = await fetchInvoiceDetailHeader(filter.id);
  const collects = await composeInvoiceCollectsPage({
    id: filter.id,
    header,
    page: filter.page ?? 1,
    pageSize: filter.page_size ?? 10,
    lookups,
  });
  return { invoice: toInvoiceDetail(header), breakdown: header.breakdown, approval: header.approval, collects };
}
